/**
 * Contract version management hook
 * Provides operations for managing contract versions and scheduling changes
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import type {
  billing_ContractVersionResponse,
} from '@/api';
import { contractVersionKeys } from '@/hooks/useVersionOperations';


/**
 * Hook to fetch all versions for a contract group
 */
export function useContractVersions(groupId?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: contractVersionKeys.group(groupId || ''),
    queryFn: async (): Promise<billing_ContractVersionResponse[]> => {
      if (!groupId) return [];

      const { ContractsService } = await import('@/api');
      const response = await ContractsService.getApiV1ContractsGroupVersions(groupId);
      // `versions` is not on the wire: ContractVersionListResponse's only array
      // field is `data` (internal/handlers/billing/contract_version_handler.go:827).
      return response.data || [];
    },
    enabled: isSignedIn && isAuthReady && !!groupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch active versions for a contract in a specific period
 */
export function useActiveVersions(contractId?: string, start?: string, end?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: contractVersionKeys.activeVersions(contractId || '', start, end),
    queryFn: async (): Promise<billing_ContractVersionResponse[]> => {
      if (!contractId) return [];

      const { ContractsService } = await import('@/api');
      const response = await ContractsService.getApiV1ContractsActiveVersions(contractId, start, end);
      // `versions` is not on the wire: ContractVersionListResponse's only array
      // field is `data` (internal/handlers/billing/contract_version_handler.go:827).
      return response.data || [];
    },
    enabled: isSignedIn && isAuthReady && !!contractId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}


